import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";

/** Los tres valores del formulario: "" = Sin verificar, que NO es lo mismo que "no". */
export type RespuestaDeRetasacion = "" | "si" | "no";

const OPCIONES: { valor: RespuestaDeRetasacion; etiqueta: string }[] = [
  { valor: "", etiqueta: "Sin verificar" },
  { valor: "si", etiqueta: "Sí, hay una anterior" },
  { valor: "no", etiqueta: "No hay" },
];

interface SegmentoDeRetasacionProps
  extends Omit<ComponentProps<"div">, "onChange" | "role" | "children"> {
  value: RespuestaDeRetasacion;
  onChange: (valor: RespuestaDeRetasacion) => void;
}

/**
 * "¿La propiedad cuenta con tasación anterior?" como control segmentado de
 * tres posiciones (RF-E3).
 *
 * Es un `radiogroup` y no tres botones sueltos: lo que se elige es UNA
 * respuesta entre tres, y "Sin verificar" es una de ellas — un valor que se
 * guarda y se imprime distinto de "No hay", no un campo en blanco.
 *
 * Recibe y reparte el resto de las props porque `FormControl` del DS le inyecta
 * `id` y los `aria-*` del campo.
 */
export function SegmentoDeRetasacion({
  value,
  onChange,
  className,
  ...resto
}: SegmentoDeRetasacionProps) {
  return (
    <div
      role="radiogroup"
      aria-label="¿La propiedad cuenta con tasación anterior?"
      className={cn("bg-muted flex w-full max-w-sm gap-[3px] rounded-md p-[3px]", className)}
      {...resto}
    >
      {OPCIONES.map(({ valor, etiqueta }) => (
        <button
          key={etiqueta}
          type="button"
          role="radio"
          aria-checked={value === valor}
          onClick={() => onChange(valor)}
          className={cn(
            "focus-visible:ring-ring/50 flex-1 rounded-sm px-1 py-[0.4375rem] text-center text-xs whitespace-nowrap outline-none focus-visible:ring-[3px]",
            value === valor ? "bg-card text-ink font-bold shadow-sm" : "text-ink-muted font-semibold",
          )}
        >
          {etiqueta}
        </button>
      ))}
    </div>
  );
}
